import { Component, OnInit, OnDestroy, CUSTOM_ELEMENTS_SCHEMA, ViewChild, Input } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { pdfDefaultOptions } from 'ngx-extended-pdf-viewer';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { MessageService, ConfirmationService } from 'primeng/api';
import { Loading } from 'notiflix';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ToastModule } from 'primeng/toast';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { PdfFormService } from './pdf-form.service';
import { JOB_STATUS } from '../jobs/job-status.enum';
import { ISqlJob } from '../jobs/sql-job.interface';
@Component({
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [
      NgxExtendedPdfViewerModule,
      CardModule,
      CommonModule,
      NgIf,
      ButtonModule,
      ConfirmDialogModule,
      ToastModule,
      DividerModule,
      CheckboxModule,
      FormsModule,
    ],
    providers: [MessageService, ConfirmationService, PdfFormService],
    selector: 'pdf-form',
    templateUrl: './pdf-form.component.html',
    styleUrls: ['./pdf-form.component.scss'],
})
export class PdfFormComponent implements OnInit {
    @ViewChild('pdfViewer') pdfViewer: any;
    
    url = '';
    pdfInitialized = false;
    formData: any;
    hasUnsavedChanges = false;
    readyToDeliver = false;
    scJob: ISqlJob = {} as ISqlJob;
    JOB_STATUS = JOB_STATUS;
    constructor(
        private config: DynamicDialogConfig,
        private confirmationService: ConfirmationService,
        private messageService: MessageService,
        private ref: DynamicDialogRef,
        private pdfFormService: PdfFormService,
    ) {
        pdfDefaultOptions.assetsFolder = 'assets';
        pdfDefaultOptions.defaultZoomValue = 'page-actual';
        this.url = this.config.data.url;
    }

    ngOnInit() {
        this.scJob = this.config.data.job;
        console.log('PDF Form Component initialized');    
    }

    ngOnDestroy() {
        console.log('PDF Form Component destroyed');
    }

    onFormDataChange(event: any) {
        this.hasUnsavedChanges = true;
        this.formData = event;
    }

    savePdfData() {
        try {
            this.confirmationService.confirm({
                key: 'pdfFormConfirmDialog',
                header: this.readyToDeliver ? 'Ready to Deliver' : 'Save Changes',
                message: this.readyToDeliver 
                    ? 'Are you sure you want to save and mark this report as ready to deliver? This action cannot be undone.'
                    : 'Are you sure you want to save your PDF form changes?',
                icon: 'pi pi-exclamation-question',
                reject: () => {
                    // Do nothing on reject
                },
                accept: async () => {
                    try {
                        Loading.standard('Saving PDF form changes...');
                        
                        if(this.readyToDeliver && !this.formData) {
                            this.setFormData(this.pdfViewer.formSupport.formData);
                        }
                        
                        const updatedJob = await this.saveFormData(this.readyToDeliver);
                        
                        if (this.readyToDeliver) {
                            this.config.data.job.scJobStatusId = JOB_STATUS.READY_TO_DELIVER;
                        }
                        
                        this.hasUnsavedChanges = false;
                        if (this.readyToDeliver) {
                            this.messageService.add({
                                key: 'pdfFormToast',
                                severity: 'success',
                                summary: 'Report Ready to Deliver',
                                detail: 'The report has been saved and marked as ready to deliver.'
                            });
                            this.ref.close();
                        } else {
                            this.messageService.add({
                                key: 'pdfFormToast',
                                severity: 'success',
                                summary: 'PDF Form Changes Saved',
                                detail: 'Your changes have been saved successfully.'
                            });
                        }
                    } catch (error: any) {
                        this.messageService.add({
                            key: 'pdfFormToast',
                            severity: 'error',
                            summary: 'Unable to save PDF form changes...',
                            detail: `The form was not saved. Please try again or contact IT support for help. ${error.message}`
                        });
                    } finally {
                        Loading.remove();
                    }
                }        
            });
        } catch (error: any) {
            console.error(`Error saving PDF form data: ${error.message}`);
        }
    }

    closePdfForm() {
        if (this.hasUnsavedChanges) {
            this.confirmationService.confirm({
                key: 'pdfFormConfirmDialog',
                header: 'Close PDF Form',
                message: `You have unsaved changes. Are you sure you want to close the PDF form?`,
                icon: 'pi pi-exclamation-question',
                reject: () => {
                    // Do nothing on reject
                },
                accept: () => {
                    this.ref.close();
                }        
            });
        } else {
            this.ref.close();
        }
    }

    setFormData(formData: any) {
        this.formData = formData;
    }
    
    async saveFormData(readyToDeliver: boolean = false) {
        if (!this.pdfViewer) {
            throw new Error('PDF viewer not initialized');
        }

        try {
            // Get the form data from the formData property that's updated by the formDataChange event
            if (!this.formData) {
                throw new Error('No form data available');
            }

            if (!this.config.data.jobId) {
                throw new Error('No job ID available');
            }

            console.log('Saving form data...', this.formData);
            
            await this.pdfFormService.savePdfForm(this.formData, this.config.data.jobId, readyToDeliver);
            
            return this.formData;
        } catch (error) {
            console.error('Error saving form data:', error);
            throw error;
        }
    }

    async setReadyToDeliver() {
        try {
            this.confirmationService.confirm({
                key: 'pdfFormConfirmDialog',
                header: 'Ready to Deliver',
                message: 'Are you sure you want to mark this report as ready to deliver? This action cannot be undone.',
                icon: 'pi pi-exclamation-question',
                reject: () => {
                    // Do nothing on reject
                },
                accept: async () => {
                    try {
                        Loading.standard('Getting job ready to deliver...');

                        await this.pdfFormService._setReadyToDeliver(this.config.data.jobId);
                        this.config.data.job.scJobStatusId = JOB_STATUS.READY_TO_DELIVER

                        this.messageService.add({
                            key: 'pdfFormToast',
                            severity: 'success',
                            summary: 'Report Ready to Deliver',
                            detail: 'The report has been saved and marked as ready to deliver.'
                        });
                        this.ref.close();
                    } catch (error: any) {
                        this.messageService.add({
                            key: 'pdfFormToast',
                            severity: 'error',
                            summary: 'Unable to mark report as ready to deliver...',
                            detail: `The job could not be marked as ready to deliver. Please try again or contact IT support for help. ${error.message}`
                        });
                    } finally {
                        Loading.remove();
                    }
                }        
            });
        } catch (error: any) {
            console.error(`Error marking job as ready to deliver: ${error.message}`);
        }
    }
}
